import {
  Building2,
  Megaphone,
  Activity,
  BarChart3,
  BookOpen,
  ChevronDown,
  ClipboardList,
  Database,
  FileUser,
  Headphones,
  LayoutDashboard,
  Menu,
  ReceiptText,
  ShieldCheck,
  ShieldEllipsis,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { adminPaths } from "@/app/router/paths";
import { useAdminSession } from "@/modules/auth/context/sessionContext";
import { SignOutButton } from "@/modules/auth/components/SignOutButton";
import { TawzeefLogo } from "@/shared/components/TawzeefLogo";

const navigation: Array<{ label: string; path: string; permission?: string; permissions?: string[]; icon: typeof LayoutDashboard }> = [
  { label: "Dashboard", path: adminPaths.dashboard, permission: "reports.view", icon: LayoutDashboard },
  { label: "Reports", path: adminPaths.reports, permission: "reports.view", icon: BarChart3 },
  { label: "Announcements", path: adminPaths.announcements, permission: "notifications.broadcast", icon: Megaphone },
  { label: "Users", path: adminPaths.users, permission: "users.view", icon: Users },
  { label: "Organizations", path: adminPaths.organizations, permission: "organizations.view", icon: Building2 },
  { label: "Jobs", path: adminPaths.jobs, permission: "jobs.view", icon: ShieldCheck },
  { label: "Applications", path: adminPaths.applications, permission: "applications.view", icon: FileUser },
  { label: "Courses", path: adminPaths.courses, permission: "courses.view", icon: BookOpen },
  { label: "Reference data", path: adminPaths.referenceData, permissions: ["industries.manage", "job_titles.manage", "skills.manage", "countries.manage", "currencies.manage", "salary_options.manage"], icon: Database },
  { label: "Billing", path: adminPaths.billing, permission: "billing.view", icon: ReceiptText },
  { label: "Privacy", path: adminPaths.privacy, permission: "privacy.view", icon: ShieldEllipsis },
  { label: "Support", path: adminPaths.support, permission: "support.view", icon: Headphones },
  { label: "Audit logs", path: adminPaths.auditLogs, permission: "audit_logs.view", icon: ClipboardList },
  { label: "Operations", path: adminPaths.operations, permission: "settings.manage", icon: Activity },
];

export function AdminLayout() {
  const { user, can } = useAdminSession();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileMenu = useRef<HTMLDivElement>(null);
  const profileTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!profileOpen) return;
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !profileMenu.current?.contains(event.target)) setProfileOpen(false);
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setProfileOpen(false);
      profileTrigger.current?.focus();
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissOnEscape);
    };
  }, [profileOpen]);
  return (
    <div className="admin-shell">
      <button
        aria-label="Close navigation"
        className={`sidebar-scrim ${open ? "sidebar-scrim--open" : ""}`}
        onClick={() => setOpen(false)}
        type="button"
      />
      <aside className={`sidebar ${open ? "sidebar--open" : ""}`}>
        <div className="sidebar__header">
          <TawzeefLogo light />
          <button aria-label="Close navigation" className="icon-button sidebar__close" onClick={() => setOpen(false)} type="button"><X /></button>
        </div>
        <nav className="sidebar__nav" aria-label="Admin navigation">
          <p>Workspace</p>
          {navigation.filter((item) => item.permission ? can(item.permission) : item.permissions?.some(can)).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink className={({ isActive }) => `nav-item ${isActive ? "nav-item--active" : ""}`} key={item.path} onClick={() => setOpen(false)} to={item.path}>
                <Icon size={16} aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar__footer">
          <span className="environment-dot" />
          <span>Secure operations workspace</span>
        </div>
      </aside>
      <div className="admin-shell__main">
        <header className="topbar">
          <button aria-label="Open navigation" className="icon-button mobile-menu" onClick={() => setOpen(true)} type="button"><Menu /></button>
          <div className="topbar__identity">
            <Link aria-label="Tawzeef admin dashboard" className="topbar__logo" to={adminPaths.dashboard}><TawzeefLogo compact /></Link>
            <div className="topbar__context"><span>Administration</span><strong>Control center</strong></div>
          </div>
          <div className="profile-menu" ref={profileMenu}>
            <button className="profile-trigger" ref={profileTrigger} aria-label={`${user?.first_name ?? ""} ${user?.last_name ?? ""} account`} aria-expanded={profileOpen} aria-controls="admin-account-menu" onClick={() => setProfileOpen((value) => !value)} type="button">
              <span className="avatar">{user?.first_name[0]}{user?.last_name[0]}</span>
              <span className="profile-trigger__text"><strong>{user?.first_name} {user?.last_name}</strong><small>Administrator</small></span>
              <ChevronDown className="profile-trigger__chevron" size={16} aria-hidden="true" />
            </button>
            {profileOpen ? (
              <div className="profile-popover" id="admin-account-menu" role="region" aria-label="Administrator account">
                <div className="profile-popover__header">
                  <span className="avatar profile-popover__avatar" aria-hidden="true">{user?.first_name[0]}{user?.last_name[0]}</span>
                  <div className="profile-popover__identity">
                    <strong>{user?.first_name} {user?.last_name}</strong>
                    <span className="profile-popover__role"><ShieldCheck size={12} aria-hidden="true" />Administrator</span>
                  </div>
                  <p className="profile-popover__email">{user?.email}</p>
                </div>
                <div className="profile-popover__actions">
                  <SignOutButton className="profile-popover__signout" />
                  <p className="profile-popover__caption">Sign out of this browser session.</p>
                </div>
              </div>
            ) : null}
          </div>
        </header>
        <main className="workspace"><Outlet /></main>
      </div>
    </div>
  );
}
